import React, { useState } from 'react';
import type { TemplateModel, TemplatePagePreset, WebsitePage } from '@troia-v3/types';
import { Icon } from '@/shared/icons';
import { Button } from '@/shared/components/ui/Button';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Modal, ModalHeader, ModalContent, ModalFooter } from '@/shared/components/ui/Modal';
import { presetsForPage } from './applyPreset';

interface PanelChooseLayoutProps {
  page: WebsitePage;
  model: TemplateModel;
  onApply: (preset: TemplatePagePreset, applyColors: boolean) => void;
}

/** Grade de layouts prontos (presets) para a página atual — com confirmação antes de trocar os blocos. */
export const PanelChooseLayout: React.FC<PanelChooseLayoutProps> = ({ page, model, onApply }) => {
  const presets = presetsForPage(model, page.pageId);
  const [selected, setSelected] = useState<TemplatePagePreset | null>(null);
  const [applyColors, setApplyColors] = useState(true);
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, boolean>>({});

  const openConfirm = (preset: TemplatePagePreset) => {
    setApplyColors(true);
    setSelected(preset);
  };

  const closeConfirm = () => setSelected(null);

  const confirm = () => {
    if (!selected) return;
    onApply(selected, applyColors);
    setSelected(null);
  };

  return (
    <div className="space-y-2" data-testid="panel-choose-layout">
      {presets.map((preset) => {
        const thumbBroken = brokenThumbs[preset.id];
        return (
          <div
            key={preset.id}
            className="flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg border"
            style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-card-background)' }}
            data-testid={`layout-preset-${preset.id}`}
          >
            {preset.thumbnail && !thumbBroken ? (
              <img
                src={preset.thumbnail}
                alt={preset.name}
                className="w-12 h-9 rounded object-cover flex-shrink-0"
                onError={() => setBrokenThumbs((prev) => ({ ...prev, [preset.id]: true }))}
              />
            ) : (
              <span
                className="w-12 h-9 rounded flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'var(--theme-page-background)' }}
              >
                <Icon name="grid" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              </span>
            )}
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                {preset.name}
              </span>
              {preset.description && (
                <span className="block text-[11px] truncate" style={{ color: 'var(--theme-muted-text)' }}>
                  {preset.description}
                </span>
              )}
            </span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => openConfirm(preset)}
              data-testid={`layout-preset-apply-${preset.id}`}
            >
              Aplicar
            </Button>
          </div>
        );
      })}

      {selected && (
        <Modal isOpen onClose={closeConfirm}>
          <ModalHeader title={`Aplicar o layout ${selected.name}?`} onClose={closeConfirm} />
          <ModalContent>
            <p className="text-sm" style={{ color: 'var(--theme-text)' }}>
              Isso substitui os blocos desta página. Textos, cores e menu do site não mudam.
            </p>
            {selected.suggestedGlobalConfig && (
              <div className="mt-3">
                <Checkbox
                  label="Aplicar também as cores sugeridas (fundo claro)"
                  checked={applyColors}
                  onChange={setApplyColors}
                  data-testid="layout-preset-apply-colors"
                />
              </div>
            )}
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={closeConfirm} data-testid="layout-preset-cancel">
              Cancelar
            </Button>
            <Button type="button" variant="primary" onClick={confirm} data-testid="layout-preset-confirm">
              Confirmar
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};
